import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, Edit3, CalendarPlus, Loader2, X } from 'lucide-react';
import { TableLoader } from '../Common/Loader';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  setPage,
  fetchIncentives,
} from '../../store/incentiveSlice';
import { extendIncentive } from '../../api/incentive';

export const IncentiveTable: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { incentives, loading, totalEntries, filters } = useAppSelector(
    (state) => state.incentive
  );

  // Extend Modal State
  const [isExtendModalOpen, setIsExtendModalOpen] = useState(false);
  const [targetExtendIncentive, setTargetExtendIncentive] = useState<{ id: string; name: string } | null>(null);
  const [extendedDays, setExtendedDays] = useState<number>(15);
  const [remarks, setRemarks] = useState<string>('Extended validity');
  const [submittingExtend, setSubmittingExtend] = useState(false);
  const [extendError, setExtendError] = useState<string | null>(null);

  const handleView = (id: string) => {
    navigate(`/incentive/details/${id}`);
  };

  const handleEdit = (id: string) => {
    navigate(`/incentive/edit/${id}`);
  };

  // Open Extend Modal for single incentive
  const handleOpenSingleExtend = (id: string, name: string) => {
    setTargetExtendIncentive({ id, name });
    setExtendedDays(15);
    setRemarks('Extended validity');
    setExtendError(null);
    setIsExtendModalOpen(true);
  };

  const handleSubmitExtend = async (e: React.FormEvent) => {
    e.preventDefault();
    setExtendError(null);

    if (!targetExtendIncentive) return;

    if (extendedDays <= 0) {
      setExtendError('Please enter a valid positive number of extension days.');
      return;
    }

    try {
      setSubmittingExtend(true);
      await extendIncentive(targetExtendIncentive.id, extendedDays, remarks);
      setIsExtendModalOpen(false);
      dispatch(fetchIncentives());
    } catch (err: any) {
      setExtendError(err?.message || 'Failed to extend validity. Please try again.');
    } finally {
      setSubmittingExtend(false);
    }
  };

  const handlePageChange = (newPage: number) => {
    dispatch(setPage(newPage));
    dispatch(fetchIncentives());
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden mb-6">
      {/* Table Section Top Bar */}
      <div className="flex flex-wrap items-center justify-between px-5 py-4 gap-3 border-b border-gray-100 bg-slate-50/50">
        <h2 className="text-base md:text-lg font-bold text-gray-800 tracking-tight m-0">
          Active & Planned Programs
        </h2>
      </div>

      {/* Table Content */}
      <div className="overflow-x-auto select-none">
        <table className="w-full text-left border-collapse min-w-[950px]">
          <thead>
            <tr className="bg-[#FFF9E6] text-gray-700 text-xs font-semibold">
              <th className="py-3.5 px-4">Program</th>
              <th className="py-3.5 px-4">Zone</th>
              <th className="py-3.5 px-4">Distributor Type</th>
              <th className="py-3.5 px-4">Payout</th>
              <th className="py-3.5 px-4">Product</th>
              <th className="py-3.5 px-4">Target</th>
              <th className="py-3.5 px-4">Incentive</th>
              <th className="py-3.5 px-4">Period</th>
              <th className="py-3.5 px-4 text-center">Status</th>
              <th className="py-3.5 px-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 text-xs md:text-sm">
            {loading ? (
              <TableLoader colSpan={10} text="Loading incentive programs..." />
            ) : incentives.length === 0 ? (
              <tr>
                <td colSpan={10} className="py-10 text-center text-gray-500">
                  No incentive programs found.
                </td>
              </tr>
            ) : (
              incentives.map((item) => {
                return (
                  <tr
                    key={item.id}
                    className="hover:bg-gray-50/80 transition-colors"
                  >
                    {/* Program Column (Name + Code) */}
                    <td className="py-3.5 px-4">
                      <div className="font-bold text-gray-900">
                        {item.name}
                      </div>
                      <div className="text-[11px] text-gray-400 font-mono mt-0.5">
                        {item.code || `INC-2025-${item.id}`}
                      </div>
                    </td>

                    {/* Region / Zone Column */}
                    <td className="py-3.5 px-4 text-gray-800 font-medium">
                      {item.zoneCity || item.zone || 'All Zones'}
                    </td>

                    {/* Distributor Type Column */}
                    <td className="py-3.5 px-4 text-gray-800 font-medium">
                        {item.distType || '-'}
                    </td>

                    {/* Payout Column */}
                    <td className="py-3.5 px-4 text-gray-800 font-semibold">
                      {item.payout}
                    </td>

                    {/* Product Column */}
                    <td className="py-3.5 px-4 text-gray-700 font-medium">
                      {item.product}
                    </td>

                    {/* Target Column */}
                    <td className="py-3.5 px-4 text-gray-600 font-medium">
                      {item.target}
                    </td>

                    {/* Reward Column */}
                    <td className="py-3.5 px-4 font-bold text-emerald-700">
                      {item.reward}
                    </td>

                    {/* Period Column */}
                    <td className="py-3.5 px-4 text-gray-600 whitespace-nowrap">
                      {item.period || item.validity}
                    </td>

                    {/* Status Column (Active -> Green, Inactive -> Red) */}
                    <td className="py-3.5 px-4 text-center text-xs">
                      <span
                        className={`px-3 py-1 text-[10px] font-bold rounded-full inline-block ${
                          item.status === 'Active'
                            ? 'bg-[#16A34A] text-white'
                            : item.status === 'Upcoming'
                            ? 'bg-amber-500 text-white'
                            : 'bg-[#B71C1C] text-white'
                        }`}
                      >
                        {item.status}
                      </span>
                    </td>

                    {/* Actions Column */}
                    <td className="py-3.5 px-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => handleView(item.id)}
                          className="p-1.5 text-slate-500 hover:text-[#B71C1C] hover:bg-slate-100 rounded-lg transition-colors cursor-pointer"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleEdit(item.id)}
                          className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer"
                          title="Edit Program"
                        >
                          <Edit3 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleOpenSingleExtend(item.id, item.name)}
                          className="p-1.5 text-slate-500 hover:text-amber-600 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer"
                          title="Extend Validity"
                        >
                          <CalendarPlus className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Footer */}
      <div className="flex flex-wrap items-center justify-between px-5 py-4 border-t border-gray-100 text-xs text-gray-500 gap-3">
        <div>
          Showing {incentives.length > 0 ? 1 : 0} to {incentives.length} of {totalEntries} programs
        </div>
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => handlePageChange(Math.max(1, filters.page - 1))}
            disabled={filters.page === 1}
            className="px-3.5 py-1.5 bg-[#FFF9E6] hover:bg-[#FFF2C6] text-amber-900 rounded-full font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer"
          >
            Previous
          </button>
          <button
            className="w-7 h-7 bg-[#B71C1C] text-white font-bold rounded-full flex items-center justify-center cursor-default shadow-2xs"
          >
            {filters.page}
          </button>
          <button
            onClick={() => handlePageChange(filters.page + 1)}
            disabled={incentives.length < (filters.limit || 20)}
            className="px-3.5 py-1.5 bg-[#FFF9E6] hover:bg-[#FFF2C6] text-amber-900 rounded-full font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer"
          >
            Next
          </button>
        </div>
      </div>

      {/* Extend Modal */}
      {isExtendModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs animate-fadeIn">
          <div className="bg-white rounded-3xl max-w-md w-full p-6 shadow-2xl border border-slate-100 space-y-5">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-2xl bg-amber-100 text-amber-800 flex items-center justify-center">
                  <CalendarPlus className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-base font-extrabold text-slate-800 tracking-tight m-0">
                    Extend Program Validity
                  </h3>
                  <p className="text-xs text-slate-500 font-medium">
                    {targetExtendIncentive?.name}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setIsExtendModalOpen(false)}
                className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 flex items-center justify-center transition-colors cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {extendError && (
              <div className="p-3 bg-rose-50 border border-rose-200 text-rose-700 text-xs font-semibold rounded-xl">
                {extendError}
              </div>
            )}

            <form onSubmit={handleSubmitExtend} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">
                  Extension Days*
                </label>
                <div className="flex items-center gap-2 mb-2">
                  {[15, 30, 45, 60].map((d) => (
                    <button
                      key={d}
                      type="button"
                      onClick={() => setExtendedDays(d)}
                      className={`px-3 py-1.5 rounded-xl text-xs font-bold border transition-all cursor-pointer ${
                        extendedDays === d
                          ? 'go_erp-bg text-white border-[#C1272D]'
                          : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100'
                      }`}
                    >
                      +{d} Days
                    </button>
                  ))}
                </div>
                <input
                  type="number"
                  required
                  min={1}
                  value={extendedDays}
                  onChange={(e) => setExtendedDays(Number(e.target.value))}
                  placeholder="Enter custom number of days"
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-xs text-slate-800 font-semibold focus:outline-none focus:border-[#C1272D] focus:bg-white transition-all"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">
                  Extension Remarks
                </label>
                <textarea
                  rows={3}
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  placeholder="Reason for extension..."
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3 text-xs text-slate-800 font-semibold focus:outline-none focus:border-[#C1272D] focus:bg-white transition-all"
                />
              </div>

              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setIsExtendModalOpen(false)}
                  className="px-4 py-2 border border-slate-300 rounded-xl text-xs font-semibold text-slate-600 hover:bg-slate-50 transition-colors cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submittingExtend}
                  className="px-5 py-2 bg-[#C1272D] hover:bg-[#A81E23] text-white text-xs font-bold rounded-xl transition-all shadow-md flex items-center gap-2 cursor-pointer disabled:opacity-50"
                >
                  {submittingExtend && <Loader2 className="w-4 h-4 animate-spin" />}
                  <span>{submittingExtend ? 'Extending...' : 'Confirm Extension'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

